import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  resolveOrganizationId,
  type AdminQueryKey,
  type OrganizationIdInput,
  type ScopedAdminQueryKey,
} from '@/lib/organization-scoping'
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { reportQueryKeys } from '@/lib/services/reports/reports.query'
import { useParams } from '@tanstack/react-router'
import {
  adjustCashDrawer,
  closeCashDrawer,
  createCashDrawerWorkstation,
  getCashDrawerStatus,
  getCashDrawerSettings,
  getCashDrawerLocations,
  getCashDrawerWorkstations,
  getCurrentCashDrawer,
  getCashDrawerSessionNavigation,
  getOpenCashDrawers,
  startCashDrawer,
  updateCashDrawerSettings,
  updateCashDrawerWorkstation,
} from './cash-drawers.api'

import type {
  OrganizationScope,
  CashDrawerIdInput,
  CashDrawerSettings,
  CashDrawerStatus,
  OpenCashDrawers,
  CurrentCashDrawer,
  CashDrawerLocation,
  CashDrawerWorkstation,
  CashDrawerSessionNavigation,
} from './cash-drawers.types'
import { isRecord } from '@/lib/utils'

interface CurrentDrawerScope extends OrganizationScope {
  workstationId?: CashDrawerIdInput
  sessionId?: CashDrawerIdInput
}

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OptionalOrganizationScope {
  organizationId?: OrganizationIdInput
}

type CashDrawerQueryParams<TData> = OptionalOrganizationScope &
  Partial<Omit<UseQueryOptions<TData, Error, TData, ScopedAdminQueryKey>, 'queryKey' | 'queryFn'>>

interface CashDrawerMutationOptions {
  invalidateOrganizationOnSuccess?: boolean
  invalidateFinancialsOnSuccess?: boolean
  invalidateFinancialsOnError?: boolean
  shouldInvalidateOnError?: (error: unknown) => boolean
}

export const cashDrawerQueryKeys = {
  all: ({ organizationId }: OptionalOrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'cash-drawer' }, { organizationId }),
  ],
  settings: ({ organizationId }: OrganizationScope) =>
    buildScopedAdminQueryKey(cashDrawerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'settings',
    }),
  status: ({ organizationId }: OrganizationScope) =>
    buildScopedAdminQueryKey(cashDrawerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'status',
    }),
  open: ({ organizationId }: OrganizationScope) =>
    buildScopedAdminQueryKey(cashDrawerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'open',
    }),
  locations: ({ organizationId }: OrganizationScope) =>
    buildScopedAdminQueryKey(cashDrawerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'locations',
    }),
  workstations: ({ organizationId }: OrganizationScope) =>
    buildScopedAdminQueryKey(cashDrawerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'workstations',
    }),
  navigation: ({ organizationId, workstationId, sessionId }: CurrentDrawerScope) =>
    buildScopedAdminQueryKey(cashDrawerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'navigation',
      workstationId,
      sessionId,
    }),
  current: ({ organizationId, workstationId }: CurrentDrawerScope) =>
    buildScopedAdminQueryKey(cashDrawerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'current',
      workstationId,
    }),
}

export const cashDrawerQueries = {
  settings: ({ organizationId }: OrganizationScope) => ({
    queryKey: cashDrawerQueryKeys.settings({ organizationId }),
    queryFn: getCashDrawerSettings,
  }),
  status: ({ organizationId }: OrganizationScope) => ({
    queryKey: cashDrawerQueryKeys.status({ organizationId }),
    queryFn: getCashDrawerStatus,
  }),
  open: ({ organizationId }: OrganizationScope) => ({
    queryKey: cashDrawerQueryKeys.open({ organizationId }),
    queryFn: getOpenCashDrawers,
  }),
  locations: ({ organizationId }: OrganizationScope) => ({
    queryKey: cashDrawerQueryKeys.locations({ organizationId }),
    queryFn: getCashDrawerLocations,
  }),
  workstations: ({ organizationId }: OrganizationScope) => ({
    queryKey: cashDrawerQueryKeys.workstations({ organizationId }),
    queryFn: getCashDrawerWorkstations,
  }),
  navigation: ({ organizationId, workstationId, sessionId }: CurrentDrawerScope) => ({
    queryKey: cashDrawerQueryKeys.navigation({ organizationId, workstationId, sessionId }),
    queryFn: getCashDrawerSessionNavigation,
    enabled: Boolean(workstationId),
  }),
  current: ({ organizationId, workstationId }: CurrentDrawerScope) => ({
    queryKey: cashDrawerQueryKeys.current({ organizationId, workstationId }),
    queryFn: getCurrentCashDrawer,
    enabled: Boolean(workstationId),
  }),
}

function useOrganizationId(explicitOrganizationId?: OrganizationIdInput) {
  const params: unknown = useParams({ strict: false })
  const orgId = isRecord(params) ? params.orgId : undefined
  return resolveOrganizationId(
    explicitOrganizationId,
    typeof orgId === 'number' || typeof orgId === 'string' ? orgId : undefined
  )
}

export function useCashDrawerSettings(params: CashDrawerQueryParams<CashDrawerSettings> = {}) {
  const organizationId = useOrganizationId(params.organizationId)
  return useQuery({ ...cashDrawerQueries.settings({ organizationId }), ...params })
}

export function useCashDrawerStatus(params: CashDrawerQueryParams<CashDrawerStatus> = {}) {
  const organizationId = useOrganizationId(params.organizationId)
  return useQuery({ ...cashDrawerQueries.status({ organizationId }), ...params })
}

export function useOpenCashDrawers(params: CashDrawerQueryParams<OpenCashDrawers> = {}) {
  const organizationId = useOrganizationId(params.organizationId)
  return useQuery({ ...cashDrawerQueries.open({ organizationId }), ...params })
}

export function useCashDrawerLocations(params: CashDrawerQueryParams<CashDrawerLocation[]> = {}) {
  const organizationId = useOrganizationId(params.organizationId)
  return useQuery({ ...cashDrawerQueries.locations({ organizationId }), ...params })
}

export function useCashDrawerWorkstations(
  params: CashDrawerQueryParams<CashDrawerWorkstation[]> = {}
) {
  const organizationId = useOrganizationId(params.organizationId)
  return useQuery({ ...cashDrawerQueries.workstations({ organizationId }), ...params })
}

export function useCurrentCashDrawer(
  workstationId: CashDrawerIdInput,
  params: CashDrawerQueryParams<CurrentCashDrawer> = {}
) {
  const organizationId = useOrganizationId(params.organizationId)
  return useQuery({
    ...cashDrawerQueries.current({ organizationId, workstationId }),
    ...params,
  })
}

function hasCashDrawerValidationError(error: unknown, ...fields: string[]) {
  const response = isRecord(error) ? error.response : undefined
  const data = isRecord(response) ? response.data : undefined
  const errors = isRecord(data) && isRecord(data.errors) ? data.errors : undefined

  return fields.some((field) => Boolean(errors?.[field]))
}

function useCashDrawerMutation<TData, TVariables>(
  mutationFn: (values: TVariables & OrganizationScope) => Promise<TData>,
  {
    invalidateOrganizationOnSuccess = false,
    invalidateFinancialsOnSuccess = false,
    invalidateFinancialsOnError = false,
    shouldInvalidateOnError,
  }: CashDrawerMutationOptions = {}
) {
  const queryClient = useQueryClient()
  const organizationId = useOrganizationId()
  const invalidateCashDrawerQueries = ({ includeFinancials = false } = {}) => {
    const invalidations = [
      queryClient.invalidateQueries({
        queryKey: cashDrawerQueryKeys.all({ organizationId }),
      }),
      queryClient.invalidateQueries({
        queryKey: reportQueryKeys.cashDrawer({ organizationId }),
      }),
    ]

    if (invalidateOrganizationOnSuccess) {
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: [buildAdminQueryRoot({ entity: 'organization' }, { organizationId })],
        })
      )
    }
    if (includeFinancials) {
      for (const entity of ['cart', 'salesOrder', 'customer']) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: [buildAdminQueryRoot({ entity }, { organizationId })],
          })
        )
      }
    }

    return Promise.all(invalidations)
  }

  return useMutation({
    mutationFn: (values: TVariables) => mutationFn({ organizationId, ...values }),
    onSuccess: () =>
      invalidateCashDrawerQueries({ includeFinancials: invalidateFinancialsOnSuccess }),
    onError:
      invalidateFinancialsOnError || shouldInvalidateOnError
        ? (error) =>
            invalidateFinancialsOnError || shouldInvalidateOnError?.(error)
              ? invalidateCashDrawerQueries({ includeFinancials: invalidateFinancialsOnError })
              : undefined
        : undefined,
  })
}

export function useUpdateCashDrawerSettingsMutation() {
  return useCashDrawerMutation(updateCashDrawerSettings, { invalidateOrganizationOnSuccess: true })
}

export function useCreateCashDrawerWorkstationMutation() {
  return useCashDrawerMutation(createCashDrawerWorkstation)
}

export function useUpdateCashDrawerWorkstationMutation() {
  return useCashDrawerMutation(updateCashDrawerWorkstation, {
    shouldInvalidateOnError: (error) =>
      hasCashDrawerValidationError(error, 'register', 'location_id'),
  })
}

export function useStartCashDrawerMutation() {
  return useCashDrawerMutation(startCashDrawer, {
    shouldInvalidateOnError: (error) =>
      hasCashDrawerValidationError(
        error,
        'cash_drawer',
        'expected_opening_cash',
        'cash_drawer_workstation_id'
      ),
  })
}

export function useAdjustCashDrawerMutation() {
  return useCashDrawerMutation(adjustCashDrawer, {
    shouldInvalidateOnError: (error) =>
      hasCashDrawerValidationError(error, 'cash_drawer', 'session'),
  })
}

export function useCloseCashDrawerMutation() {
  return useCashDrawerMutation(closeCashDrawer, {
    invalidateFinancialsOnSuccess: true,
    // Close recovery can commit financial receipts before validation or transport fails.
    invalidateFinancialsOnError: true,
  })
}

export function useCashDrawerSessionNavigation(
  workstationId: CashDrawerIdInput,
  sessionId: CashDrawerIdInput,
  params: CashDrawerQueryParams<CashDrawerSessionNavigation> = {}
) {
  const organizationId = useOrganizationId(params.organizationId)
  const queryClient = useQueryClient()
  const latestKey = cashDrawerQueryKeys.navigation({ organizationId, workstationId })
  return useQuery({
    ...cashDrawerQueries.navigation({ organizationId, workstationId, sessionId }),
    // Resolving the default latest view to its stable URL reuses the snapshot already fetched.
    initialData: () => {
      const latest = queryClient.getQueryData<CashDrawerSessionNavigation>(latestKey)
      return Number(latest?.selected?.id) === Number(sessionId) ? latest : undefined
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(latestKey)?.dataUpdatedAt,
    ...params,
  })
}
